import { useOrganization, useUser } from '@clerk/clerk-react';
import { LockKeyhole } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { configuration } from '@/constants/config';
import { HOME_TOUR_CLASSES } from '@/constants/tour/classNames';
import { isLockedFunction } from '@/lib/discovery';
import { useStageStore } from '@/stores/stages/useStageStore';
import { useAimeiChatStore } from '@/stores/useAimeiChatStore';

const AimeiChatBox = () => {
  // refs
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // state
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  const [isChatReady, setIsChatReady] = useState(false);

  // custom hooks
  const { organization } = useOrganization();
  const { user } = useUser();
  const { stageList, featureLocks, discoveryStatus } = useStageStore();
  const { promptContext, setPromptContext } = useAimeiChatStore();

  // derived values
  const isLocked = isLockedFunction(
    'aimei',
    stageList,
    featureLocks,
    discoveryStatus
  );

  // Listen for Voiceflow events to detect when chat is ready
  useEffect(() => {
    const handleVoiceflowEvents = (event: MessageEvent) => {
      if (
        event.data &&
        typeof event.data === 'string' &&
        event.data.startsWith('voiceflow:')
      ) {
        try {
          const eventType = event.data;

          // Chat is ready when it's opened or when an interaction occurs
          if (
            eventType === 'voiceflow:open' ||
            eventType === 'voiceflow:interact'
          ) {
            setIsChatReady(true);
          }
        } catch (error) {
          console.error('Error parsing Voiceflow event:', error);
        }
      }
    };

    window.addEventListener('message', handleVoiceflowEvents);

    return () => {
      window.removeEventListener('message', handleVoiceflowEvents);
    };
  }, []);

  // Handle prompt context when widget is loaded and chat is ready
  useEffect(() => {
    if (isWidgetLoaded && promptContext && window.voiceflow?.chat?.interact) {
      const sendPromptWithRetry = () => {
        try {
          // Ensure the chat is open before sending the interaction
          if (window?.voiceflow?.chat?.open) {
            window.voiceflow.chat.open();
          }

          // Small delay to ensure the chat is ready
          setTimeout(
            () => {
              try {
                window?.voiceflow?.chat?.interact?.({
                  type: 'text',
                  payload: promptContext,
                });
                // Clear the context after sending
                setPromptContext(null);
              } catch (error) {
                console.error('Failed to send prompt context (retry):', error);
                // If the first attempt fails, try opening the chat and retry after a longer delay
                if (window?.voiceflow?.chat?.open) {
                  window?.voiceflow?.chat?.open();
                  setTimeout(() => {
                    try {
                      window?.voiceflow?.chat?.interact?.({
                        type: 'text',
                        payload: promptContext,
                      });
                      setPromptContext(null);
                    } catch (retryError) {
                      console.error(
                        'Failed to send prompt context after retry:',
                        retryError
                      );
                      setPromptContext(null);
                    }
                  }, 1000);
                }
              }
            },
            isChatReady ? 200 : 500
          ); // Shorter delay if chat is already ready
        } catch (error) {
          console.error('Failed to send prompt context:', error);
          setPromptContext(null);
        }
      };

      sendPromptWithRetry();
    }
  }, [isWidgetLoaded, promptContext, setPromptContext, isChatReady]);

  useEffect(() => {
    if (isLocked || isWidgetLoaded) return;

    // Load Voiceflow widget
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.onload = () => {
      if (window.voiceflow && chatContainerRef.current) {
        window.voiceflow.chat?.load?.({
          verify: { projectID: configuration.VOICEFLOW_PROJECT_ID },
          url: 'https://general-runtime.voiceflow.com',
          // The agent runtime rejects alias values ("production"/"development");
          // only pass explicit 24-hex version IDs.
          versionID: /^[0-9a-f]{24}$/.test(configuration.VOICEFLOW_VERSION_ID)
            ? configuration.VOICEFLOW_VERSION_ID
            : undefined,
          userID: user?.id || 'unknown',
          launch: {
            event: {
              type: 'launch',
              payload: {
                userId: user?.id || 'unknown',
                zorId: organization?.id || 'unknown',
                org_id: organization?.id || 'unknown',
                org_name: organization?.name || 'unknown',
              },
            },
          },
          render: {
            mode: 'embedded',
            target: chatContainerRef.current,
          },
          user: {
            name: user?.firstName + ' ' + user?.lastName,
            image: user?.imageUrl || '',
          },
        });
        setIsWidgetLoaded(true);
      }
    };
    script.src = 'https://cdn.voiceflow.com/widget-next/bundle.mjs';
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector(
        'script[src="https://cdn.voiceflow.com/widget-next/bundle.mjs"]'
      );
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [user, isLocked, isWidgetLoaded]);

  return (
    <div
      className={`${HOME_TOUR_CLASSES.AIMEI_CHAT_BOX} w-full overflow-hidden rounded-3xl border border-border bg-muted shadow-lg`}
    >
      <div className="relative flex h-[330px] flex-col bg-muted">
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-hidden"
          style={{ height: '100%' }}
        />
        {isLocked && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/80 px-6 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-white p-6 shadow-lg">
              <LockKeyhole className="size-12 text-typo-gray-tertiary" />
              <p className="text-center text-sm text-typo-gray-tertiary">
                Complete the required discovery steps to unlock Aimei
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { AimeiChatBox };
